import { Autocomplete, Chip, TextField } from "@mui/material"
import { translate } from "app/language/service"
import React from "react"
import { MIN_FILTER_LENGTH } from "ui/model"

interface IEntitiesSelectProps<T> {
  id: string
  inputLabel: string
  idKey?: string
  inputPlaceholder?: string
  disabled?: boolean
  required?: boolean
  error?: boolean
  minFilterLength?: number
  defaultEntities?: T[]
  defaultSelectedEntities?: T[]
  defaultSelectedEntityIds?: number[]
  sx?: any
  onChange: (entities: T[]) => any
  onBlur?: () => any
  searchEntities: (filter: string) => Promise<T[]>
  getOptionLabel: (entity: T) => string
}

export const EntitiesSelect = React.memo(
  /* eslint-disable @typescript-eslint/no-unnecessary-type-constraint */
  <T extends { id: number }>({
    id,
    inputLabel,
    inputPlaceholder,
    disabled,
    required,
    error,
    minFilterLength,
    defaultEntities,
    defaultSelectedEntities,
    idKey,
    defaultSelectedEntityIds,
    sx,
    onChange,
    onBlur,
    searchEntities,
    getOptionLabel
  }: IEntitiesSelectProps<T>): JSX.Element => {
    const isFirstRenderRef = React.useRef<boolean>(true)
    const [filter, setFilter] = React.useState("")
    const [entities, setEntities] = React.useState<T[]>(defaultEntities || [])
    const [selectedEntities, setSelectedEntities] = React.useState<T[]>(defaultSelectedEntities || [])
    const [isLoading, setIsLoading] = React.useState(false)
    const [hasLoadingError, setHasLoadingError] = React.useState(false)
    const currentRequestIndexRef = React.useRef(0)
    const timeoutRef = React.useRef<any>()
    minFilterLength = typeof minFilterLength === "number" ? minFilterLength : MIN_FILTER_LENGTH
    const loadEntities = () => {
      currentRequestIndexRef.current++
      clearTimeout(timeoutRef.current)
      setIsLoading(true)
      if (hasLoadingError) {
        setHasLoadingError(false)
      }
      setEntities(defaultEntities || [])
      timeoutRef.current = setTimeout(async () => {
        const requestIndex = currentRequestIndexRef.current
        try {
          if ((!filter && minFilterLength > 0) || filter.length < (minFilterLength as number)) {
            setEntities(defaultEntities || [])
            setIsLoading(false)
          } else {
            if (requestIndex === currentRequestIndexRef.current) {
              const fetchedEntities = await searchEntities(filter)
              let isFirstRender = isFirstRenderRef.current
              if (isFirstRender) {
                isFirstRenderRef.current = false
                if (defaultSelectedEntityIds && defaultSelectedEntityIds.length > 0) {
                  setSelectedEntities(fetchedEntities.filter(e => defaultSelectedEntityIds.includes(e.id)))
                }
              }
              const selectedIds = selectedEntities.map(c => (c as any)[idKey || "id"])
              const filteredEntities = fetchedEntities.filter(c => !selectedIds.includes((c as any)[idKey || "id"]))
              setEntities(filteredEntities)
              setIsLoading(false)
            }
          }
        } catch (error) {
          console.error(error)
          if (requestIndex === currentRequestIndexRef.current) {
            setHasLoadingError(true)
            setIsLoading(false)
          }
        }
      }, 500)
    }
    React.useEffect(() => loadEntities(), [filter])
    return (
      <Autocomplete
        multiple
        disabled={disabled}
        loading={isLoading}
        sx={{ "width": "100%", "& input::placeholder": { fontSize: ".7rem" }, ...sx }}
        size="small"
        id={id}
        options={entities}
        value={selectedEntities}
        filterSelectedOptions
        onInputChange={(_event, value) => setFilter(value)}
        noOptionsText={translate(
          hasLoadingError
            ? "label.loading.error"
            : filter.length < minFilterLength
              ? "label.min.char"
              : "label.no.match",
          minFilterLength
        )}
        onChange={(_event, newSelectedEntities) => {
          if (!selectedEntities) {
            setSelectedEntities([])
            onChange([])
          } else if (!Array.isArray(newSelectedEntities)) {
            setSelectedEntities([newSelectedEntities])
            onChange([newSelectedEntities])
          } else {
            setSelectedEntities(newSelectedEntities)
            onChange(newSelectedEntities)
          }
        }}
        limitTags={2}
        renderTags={
          id
            ? (es, getTagProps) =>
                es.map((e, i) => <Chip id={`${id}-${i}`} label={getOptionLabel(e)} {...getTagProps({ index: i })} />)
            : undefined
        }
        getOptionLabel={entity => getOptionLabel(entity)}
        renderInput={params => (
          <TextField
            {...params}
            required={required}
            label={inputLabel}
            placeholder={inputPlaceholder}
            error={error}
            onBlur={onBlur}
          />
        )}
      />
    )
  }
)
